import type { AddressDto, ApplicantDto } from '@/castapi';
import { AppLogger } from '@/logger';
import type { IGetPendingApplicantsParams } from '@/castapi/helpers';
import { getAddressApi, getAwsApi, getEducationalApi, getErrorMessage } from '@/castapi/helpers';
import type { IActionParams } from '@/store/modules/index';
import type { ITableParamsState } from '@/store/modules/common/tableParamsState';
import TableParamsState from '@/store/modules/common/tableParamsState';
import { uploadImage } from './common/files';
import { router } from '@/utils';

const logger = new AppLogger('adminEdu state');

const tableParams = new TableParamsState();

type IAdminEduState = ITableParamsState & {
  isEduMode: boolean;
  pendingApplicants: ApplicantDto[];
  pendingApplicantsLoading: boolean;
  pendingApplicantsLoadError?: string;
  currentApplicant?: ApplicantDto;
  currentApplicantLoading: boolean;
  currentApplicantLoadError?: string;
  currentApplicantAddresses: AddressDto[];
  applicantDeleting: boolean;
  applicantDeleteError?: string;
};

const initialState = (): IAdminEduState => ({
  ...tableParams.state(),
  isEduMode: false,
  pendingApplicants: [],
  pendingApplicantsLoading: false,
  pendingApplicantsLoadError: undefined,
  currentApplicant: undefined,
  currentApplicantLoading: false,
  currentApplicantLoadError: undefined,
  currentApplicantAddresses: [],
  applicantDeleting: false,
  applicantDeleteError: null,
});

type ActionParams = IActionParams<IAdminEduState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    ...tableParams.mutations,
    SWITCH_EDU_MODE(state: IAdminEduState): void {
      state.isEduMode = !state.isEduMode;
    },
    PENDING_APPLICANTS_LOADING(state: IAdminEduState): void {
      state.pendingApplicantsLoading = true;
      state.pendingApplicantsLoadError = undefined;
    },
    PENDING_APPLICANTS_LOADED(state: IAdminEduState, users: ApplicantDto[]): void {
      state.pendingApplicants = users;
      state.pendingApplicantsLoading = false;
    },
    PENDING_APPLICANTS_LOAD_ERROR(state: IAdminEduState, payload: Error): void {
      state.pendingApplicantsLoading = false;
      state.pendingApplicantsLoadError = getErrorMessage(payload);
    },
    APPLICANT_LOADING(state: IAdminEduState): void {
      state.currentApplicantLoading = true;
      state.currentApplicantLoadError = undefined;
    },
    APPLICANT_LOADED(state: IAdminEduState, user: ApplicantDto): void {
      state.currentApplicant = user;
      state.currentApplicantLoading = false;
    },
    APPLICANT_LOAD_ERROR(state: IAdminEduState, payload: Error): void {
      state.currentApplicantLoading = false;
      state.currentApplicantLoadError = getErrorMessage(payload);
    },
    APPLICANT_ADDRESSES_LOADED(state: IAdminEduState, addresses: AddressDto[]): void {
      state.currentApplicantLoading = false;
      state.currentApplicantAddresses = addresses;
    },
    APPLICANT_APPROVED() {}, //Needed for subscription
    APPLICANT_REJECTED() {}, //Needed for subscription
    APPLICANT_CHANGED(state: IAdminEduState) {
      state.currentApplicantLoading = false;
    }, //Needed for subscription
    APPLICANT_CHANGE_ERROR(state: IAdminEduState, payload: Error): void {
      state.currentApplicantLoading = false;
      state.currentApplicantLoadError = getErrorMessage(payload);
    },
    APPLICANT_DELETING(state: IAdminEduState): void {
      state.applicantDeleting = true;
      state.applicantDeleteError = null;
    },
    APPLICANT_DELETED(state: IAdminEduState): void {
      state.applicantDeleting = false;
      state.applicantDeleteError = null;
    },
    APPLICANT_DELETE_ERROR(state: IAdminEduState, payload: Error): void {
      state.applicantDeleting = false;
      state.applicantDeleteError = getErrorMessage(payload);
    },
    RESET_STATE(state: IAdminEduState): void {
      Object.assign(state, initialState());
    },
  },
  actions: {
    ...tableParams.actions,
    switchEduMode({ commit }: ActionParams) {
      commit('SWITCH_EDU_MODE');
      router.push('/users');
    },
    async loadPendingApplicants(
      { commit, rootGetters }: ActionParams,
      searchParams: IGetPendingApplicantsParams,
    ): Promise<void> {
      const { limit, offset, searchText, sortBy, sortDesc, showTeachers, showStudents, showPending, showRejected } =
        searchParams || {};
      commit('PENDING_APPLICANTS_LOADING');
      try {
        const response = await getEducationalApi(
          rootGetters['login/accessToken'],
        ).educationalControllerGetPendingApplicants(
          limit,
          offset,
          searchText,
          sortBy,
          sortDesc,
          0,
          true,
          showTeachers,
          showStudents,
          showPending,
          showRejected,
        );
        commit('PENDING_APPLICANTS_LOADED', response.data);
      } catch (error) {
        commit('PENDING_APPLICANTS_LOAD_ERROR', error);
        logger.captureStoreError('loadPendingApplicants', error, { limit, offset, searchText, sortBy, sortDesc });
      }
    },
    async getApplicant({ commit, rootGetters }: ActionParams, applicantId: number): Promise<void> {
      commit('APPLICANT_LOADING');
      try {
        const response = await getEducationalApi(rootGetters['login/accessToken']).educationalControllerGetApplicant(
          applicantId,
        );
        commit('APPLICANT_LOADED', response.data);
      } catch (error) {
        commit('APPLICANT_LOAD_ERROR', error);
        logger.captureStoreError('getApplicant', error, { applicantId });
      }
    },
    async getApplicantAddresses({ commit, rootGetters }: ActionParams, userId: number): Promise<void> {
      commit('APPLICANT_LOADING');
      try {
        const response = await getAddressApi(rootGetters['login/accessToken']).addressControllerGetUserAddresses(
          userId,
        );
        commit('APPLICANT_ADDRESSES_LOADED', response.data);
      } catch (error) {
        commit('APPLICANT_LOAD_ERROR', error);
        logger.captureStoreError('getApplicantAddress', error, { userId });
      }
    },
    async approveApplicant({ commit, rootGetters }: ActionParams, { userId }: { userId: number }): Promise<void> {
      commit('APPLICANT_LOADING');
      try {
        await getEducationalApi(rootGetters['login/accessToken']).educationalControllerApproveApplicant(userId);
        commit('APPLICANT_APPROVED');
      } catch (error) {
        commit('APPLICANT_LOAD_ERROR', error);
        logger.captureStoreError('approveApplicant', error, { userId });
      }
    },
    async rejectApplicant({ commit, rootGetters }: ActionParams, { userId }: { userId: number }): Promise<void> {
      commit('APPLICANT_LOADING');
      try {
        await getEducationalApi(rootGetters['login/accessToken']).educationalControllerRejectApplicant(userId);
        commit('APPLICANT_REJECTED');
      } catch (error) {
        commit('APPLICANT_LOAD_ERROR', error);
        logger.captureStoreError('rejectApplicant', error, { userId });
      }
    },
    async deleteApplicant({ commit, rootGetters }: ActionParams, { userId }: { userId: number }): Promise<void> {
      try {
        commit('APPLICANT_DELETING');
        await getEducationalApi(rootGetters['login/accessToken']).educationalControllerDeleteApplicant(userId);
        commit('APPLICANT_DELETED');
      } catch (error) {
        commit('APPLICANT_DELETE_ERROR', error);
        logger.captureStoreError('deleteApplicant', error, { userId });
      }
    },
    async updateApplicantProofFile(
      { commit, dispatch, rootState }: ActionParams,
      { userId, proofNumber, imageFile }: { userId: number; proofNumber: 1 | 2; imageFile: string },
    ): Promise<void> {
      commit('APPLICANT_LOADING');
      try {
        const fileName = `proof${proofNumber}`;
        let imageUrl: undefined | string;
        const accessToken = rootState.login.accessToken;
        if (imageFile) {
          imageUrl = await uploadImage(imageFile, `edu-proof/${userId}/${fileName}`, getAwsApi(accessToken), false);
        }
        const response = await getEducationalApi(accessToken).educationalControllerUpdateApplicantProofFile(
          userId,
          proofNumber,
          imageUrl,
        );
        commit('APPLICANT_CHANGED', response.data);
        await dispatch('getApplicant', userId);
      } catch (error) {
        commit('APPLICANT_CHANGE_ERROR', error);
        logger.captureStoreError('updateApplicantProofFile', error, { userId, proofNumber, imageFile });
      }
    },
    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
  },
  getters: {
    ...tableParams.getters,
    isEduMode: (state: IAdminEduState): boolean => state.isEduMode,
    pendingApplicants: (state: IAdminEduState): ApplicantDto[] => state.pendingApplicants,
    pendingApplicantsLoading: (state: IAdminEduState): boolean => state.pendingApplicantsLoading,
    pendingApplicantsLoadError: (state: IAdminEduState): undefined | string => state.pendingApplicantsLoadError,
    currentApplicant: (state: IAdminEduState): ApplicantDto | undefined => state.currentApplicant,
    currentApplicantLoading: (state: IAdminEduState): boolean => state.currentApplicantLoading,
    currentApplicantLoadError: (state: IAdminEduState): undefined | string => state.currentApplicantLoadError,
    currentApplicantAddress: (state: IAdminEduState): AddressDto | undefined =>
      state.currentApplicantAddresses.find(a => a.isDefault) || state.currentApplicantAddresses.length
        ? state.currentApplicantAddresses[0]
        : undefined,
    currentApplicantCountry: (
      state: IAdminEduState,
      getters: any,
      _rootState: any,
      rootGetters: any,
    ): string | undefined =>
      getters.currentApplicantAddress
        ? rootGetters['dictionary/countries'].find(c => c.id === getters.currentApplicantAddress.countryRef)?.name
        : undefined,
    applicantDeleteError: (state: IAdminEduState): undefined | string => state.applicantDeleteError,
  },
};
